import React, { useImperativeHandle, forwardRef, useState } from 'react';
import { validateInit, validateForm } from '@assets/js/util/formValidation.js';
import { formAtt } from '@assets/js/util/commonUtil.js';
import CompFmtInput from '@components/form/fragment/comp-fmt-input.js';
import CompFmtSelectbox from '@components/form/fragment/comp-fmt-selectbox.js';
import {ApiSave} from '@assets/js/api/commonApi.js';

const CompFormMatchmaking = forwardRef((props, ref) => {
    const [params, setParams] = useState({});
    const [title, setTitle] = useState('대진관리 등록/수정');
    const [dvsnParams, setDvsnParams] = useState({});
    const [wetclsParams, setWetclsParams] = useState([]);
    
    /****************************
     * Define required variables
    *****************************/
    const rootId = 'modalSheet';
    const validateOpt = {
        scopeEle: document.querySelector('#' + rootId),
        targetIds: ['comptnCd', 'dvsnCd', 'wetclsCd', 'useYn'],
        params: params,
    }

    /*************************
     * Define react functions
    **************************/
    // Forward ref to allow parent components to call event functions.
    useImperativeHandle(ref, () => ({
        modalToggle(p_params) {
            handleToggle();
            handleValue(p_params);
            setParams(p_params || {});
        },
    }));

    /**********************
     * Define functions
    ***********************/
    const handleValue = (p_params) => {
        const targetEle = document.querySelector('#' + rootId);

        // Handle mode-specific logic
        if (p_params && p_params.mode === 'M') {
            setTitle('대진관리 수정');
            // Modify mode - disable all code fields
            const comptnCdEle = targetEle?.querySelector('#comptnCd');
            const dvsnCdEle = targetEle?.querySelector('#dvsnCd');
            const wetclsCdEle = targetEle?.querySelector('#wetclsCd');
            if (comptnCdEle) comptnCdEle.disabled = true;
            if (dvsnCdEle) dvsnCdEle.disabled = true;
            if (wetclsCdEle) wetclsCdEle.disabled = true;

            setDvsnParams({comptnCd: p_params?.comptnCd});
            setWetclsParams({comptnCd: p_params?.comptnCd});
        } else {
            setTitle('대진관리 등록');
            // Insert mode - enable code fields for selection
            const comptnCdEle = targetEle?.querySelector('#comptnCd');
            const dvsnCdEle = targetEle?.querySelector('#dvsnCd');
            const wetclsCdEle = targetEle?.querySelector('#wetclsCd');
            if (comptnCdEle) comptnCdEle.disabled = false;
            if (dvsnCdEle) {
                dvsnCdEle.disabled = false;
                setDvsnParams({});
            }
            if (wetclsCdEle) {
                wetclsCdEle.disabled = false;
                setWetclsParams({});
            }
        }
    }
    const handleToggle = () => {
        const targetEle = document.querySelector('#' + rootId);
        if (targetEle) {
            targetEle.style.display = targetEle.style.display === 'none' ? 'block' : 'none';
        }

        // Reset form inputs and validation state
        validateInit(validateOpt);

        Object.keys(formObj).forEach(key => {
            const ele = targetEle?.querySelector(`#${formObj[key].id}`);
            if (ele) { ele.value = ''; }
        });
    }
    const onSave = () => {
        // Validate form inputs before saving
        if (!validateForm(validateOpt)) { return; }
        if (params == null || params.comptnCd === '') { return; }

        if (params.mtmkgStat !== 'R') {
            alert('대기 상태에서만 생성할 수 있습니다.');
            return;
        }

        // Create mtmkgCd from individual codes
        const mtmkgCd = `${params.comptnCd || ''}-${params.dvsnCd || ''}-${params.wetclsCd || ''}`;
        const saveParams = {
            ...params,
            mtmkgCd: mtmkgCd
        };

        // Fetch data from the server
        ApiSave('/sys/mtmkgmng/save/info', saveParams).then(res => {
            if (res && res.success) {
                // Close the modal after saving
                handleToggle();
                // Search for updated data
                props.onGridSearch();
            }
        });
    }
    const onValChange = (e) => {
        const { id } = e.target;
        // Validate the specific input field
        validateForm(validateOpt, id);
    }
    const onInputChange = (e) => {
        const { id, value } = e.target;
        // Update the state with the new value only (no validation)
        setParams({ ...params, [id]: value });
    }
    
    // Handle competition code change - fetch related division and weight class data
    const onComptnCdChange = (e) => {
        const { id, value } = e.target;
        setParams({ ...params, [id]: value, dvsnCd: '', wetclsCd: '' });
        
        if (value) {
            // Fetch division and weight class options for selected competition
            setDvsnParams({comptnCd: value});
            setWetclsParams({comptnCd: value});
        } else {
            // Clear Params if no competition selected
            setDvsnParams({});
            setWetclsParams({});
        }
    }

    const onChangeMtmkgNm = (e) => {
        const { id, value } = e.target;
        const rootEle = document.getElementById(rootId);
        
        // Get selected option text for wetclsCd
        const wetclsCdElement = rootEle.querySelector('#wetclsCd');
        const wetclsText = wetclsCdElement && wetclsCdElement.value ? 
            wetclsCdElement.options[wetclsCdElement.selectedIndex].text : '';
        
        // Get selected option text for dvsnCd
        const dvsnCdElement = rootEle.querySelector('#dvsnCd');
        const dvsnText = dvsnCdElement && dvsnCdElement.value ? 
            dvsnCdElement.options[dvsnCdElement.selectedIndex].text : '';
        
        // Concatenate dvsnCd and wetclsCd text values
        const mtmkgNm = dvsnText && wetclsText ? `${dvsnText} ${wetclsText}` : '';
        
        // Update mtmkgNm input field value
        const mtmkgNmElement = rootEle.querySelector('#mtmkgNm');
        if (mtmkgNmElement) {
            mtmkgNmElement.value = mtmkgNm;
        }

        setParams({ ...params, [id]: value, mtmkgNm: mtmkgNm });
    }
    
    /****************************
     * Define form config
    *****************************/
    // Dynamic form configuration based on mode
    const isModifyMode = params.mode === 'M';
    const formObj = {
        // Top section - Code selectors (dynamic disabled based on mode)
        comptnCd : formAtt({id: 'comptnCd', label: '대회', value: params['comptnCd'] || '', disabled: isModifyMode, required: true}),
        dvsnCd   : formAtt({id: 'dvsnCd', label: '부문', value: params['dvsnCd'] || '', disabled: isModifyMode, required: true, classCol: 'col-6'}),
        wetclsCd : formAtt({id: 'wetclsCd', label: '체급', value: params['wetclsCd'] || '', disabled: isModifyMode, required: true, classCol: 'col-6'}),
        
        // Main fields
        mtmkgNm  : formAtt({id: 'mtmkgNm', label: '대진명', value: params['mtmkgNm'] || '', maxLength: 200}),
        remark   : formAtt({id: 'remark', label: '설명', value: params['remark'] || '', maxLength: 200}),
        useYn    : formAtt({id: 'useYn', label: '사용여부', value: params['useYn'] || '', required: true, classCol: 'col-4'}),
    }

    return (
        <>
            <div 
                className="modal modal-sheet p-1 py-md-5"
                style={{ display: 'none', backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
                tabIndex={-1}
                role="dialog"
                id={rootId}
            >
                <div className="modal-dialog modal-lg">
                    <div className="modal-content rounded-4 shadow">
                        <div className="modal-header border-bottom-0">
                            <h1 className="modal-title fs-5">{title}</h1>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                                onClick={handleToggle}
                            />
                        </div>
                        <div className="modal-body py-0">
                            <div className="row g-5">
                                <div className="col-lg-12">
                                    <form className="needs-validation" noValidate="">
                                        <div className="row">
                                            {/* Top section: Code selectors (disabled) */}
                                            <div className="bg-light p-3 rounded">
                                                <div className="row mb-2">
                                                    <CompFmtSelectbox 
                                                        attrs={formObj.comptnCd}
                                                        grpCd={"COMPT"}
                                                        selectType={"S"}
                                                        onChange={onComptnCdChange}
                                                    />
                                                </div>
                                                <div className="row">
                                                    <CompFmtSelectbox 
                                                        attrs={formObj.dvsnCd}
                                                        grpCd={"DVSNCD"}
                                                        selectType={"S"}
                                                        customParams={dvsnParams}
                                                        onChange={onChangeMtmkgNm}
                                                    />
                                                    <CompFmtSelectbox 
                                                        attrs={formObj.wetclsCd}
                                                        grpCd={"WETCLSCD"}
                                                        selectType={"S"}
                                                        customParams={wetclsParams}
                                                        onChange={onChangeMtmkgNm}
                                                    />
                                                </div>
                                            </div>

                                            {/* Main form fields */}
                                            <div className="p-3 rounded">
                                                <div className="row mb-2">
                                                    <CompFmtInput
                                                        attrs={formObj.mtmkgNm}
                                                        onChange={onInputChange}
                                                        onBlur={onValChange}
                                                    />
                                                </div>
                                                <div className="row mb-2">
                                                    <CompFmtInput
                                                        attrs={formObj.remark}
                                                        onChange={onInputChange}
                                                        onBlur={onValChange}
                                                    />
                                                </div>
                                                <div className="row">
                                                    <CompFmtSelectbox 
                                                        attrs={formObj.useYn}
                                                        grpCd={"GRP001"}
                                                        selectType={"S"}
                                                        onChange={(e) => {
                                                            const { id, value } = e.target;
                                                            setParams({ ...params, [id]: value });
                                                            validateForm(validateOpt, id);
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer flex-column align-items-stretch w-100 gap-2 pb-3 border-top-0">
                            <div className="btn-group">
                                <button type="button" className="btn btn-lg btn-primary" onClick={onSave}>
                                    Save changes
                                </button>
                                <button type="button" className="btn btn-lg btn-secondary" data-bs-dismiss="modal" onClick={handleToggle}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </> 
    );
});

export default CompFormMatchmaking;